import type { ColDef, Column, GridApi, IRowNode } from "ag-grid-community";
import type { TCell } from "@package/grid-utils";

import type { TRangeSelection } from "./webapp/market-grid/useRangeSelectionChange";
import { colParams } from "./webapp/utils";

export function colDefId<T>(colDef: ColDef<T>): ColDef<T> {
  return {
    ...colDef,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
  };
}

export function redrawRowsByIds(api: GridApi | null, ids: string[]) {
  if (!api) throw new Error("No api passed to redrawRowsByIds");
  const rowNodes = ids.map((id) => api.getRowNode(id)).filter(Boolean);
  api.redrawRows({ rowNodes });
}

export type TRangeCell = {
  productId?: string;
  entityId?: string;
  columnId: string;
  rowId: string;
  rowIndex: number;
  headerName: string;
  period: string;
  cellValue: string | number | undefined;
};

export function getCurrentCellValue(cell: TRangeCell, api: GridApi) {
  const rowNode = api.getRowNode(cell.rowId);
  if (!rowNode) return;
  return api.getCellValue<TCell>({ rowNode, colKey: cell.columnId });
}

export function getCurrentCellValueById(columnId: string | Column<TCell>, rowId: string, api: GridApi) {
  const rowNode = api.getRowNode(rowId);
  if (!rowNode) return;
  return api.getCellValue<TCell>({ rowNode, colKey: columnId });
}

export function getCurrentCellValueByEntityId(entityId: string, rowId: string, api: GridApi) {
  const rowNode = api.getRowNode(rowId);
  if (!rowNode) return;
  const column = api.getColumns()?.find((col) => colParams(col)?.entityId === entityId);
  if (!column) return;
  return api.getCellValue<TCell>({ rowNode, colKey: column.getColId() });
}

export function selectedCellsByRange<T, S>(
  api: GridApi<T>,
  store: S,
  processCell: (params: {
    column: Column<T>;
    entityId: string | undefined;
    cellValue: string | number | undefined;
    rowNode: IRowNode<T>;
    rangeIdx: number;
    store: S;
  }) => unknown,
) {
  const cellRanges = api.getCellRanges();
  if (!cellRanges) return store; // Handle case where ranges are null or undefined

  for (const [idx, cellRange] of cellRanges.entries()) {
    if (cellRange.columns.length === 0) {
      throw new Error("No columns found in range");
    }

    const { startRow, endRow } = cellRange;
    if (!startRow || !endRow) {
      throw new Error("No rows found in range");
    }

    const startRowIndex = Math.min(startRow.rowIndex, endRow.rowIndex);
    const endRowIndex = Math.max(startRow.rowIndex, endRow.rowIndex);

    for (const rowIndex of Array.from({ length: endRowIndex - startRowIndex + 1 }, (_, i) => startRowIndex + i)) {
      for (const column of cellRange.columns) {
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode) continue;

        const columnId = column.getColId();
        const columnParams = colParams(column);
        const entityId = columnParams?.entityId;
        const cellValue = api.getCellValue({ rowNode, colKey: columnId });

        processCell({
          column,
          entityId,
          cellValue,
          rowNode,
          store,
          rangeIdx: idx,
        });
      }
    }
  }

  return store;
}

export function getProcessedSelectedCellsByRange({ api }: { api: GridApi | null }) {
  if (!api) return [];

  return selectedCellsByRange(api, [] as TRangeSelection, ({ column, rowNode, store, rangeIdx }) => {
    if (!column || !rowNode.id || rowNode?.rowIndex === undefined || rowNode?.rowIndex === null) return;
    store[rangeIdx] = store[rangeIdx] ?? [];

    // For adhoc spreads, use the full spread identifier from rowNode.data.id
    // For regular rows, use the period
    const period = rowNode.data?.rowType === "adhoc" ? rowNode.data.id : rowNode.data.period;

    store[rangeIdx].push({
      entityId: column?.getColDef()?.headerComponentParams?.entityId,
      columnId: column.getId(),
      productId: column?.getColDef().headerComponentParams.productId,
      rowId: rowNode.id,
      rowIndex: rowNode.rowIndex,
      headerName: column?.getColDef().headerName || column.getId(),
      period,
      cellValue: rowNode.data.cellValue,
    });
  });
}
